/**
 * A tmux session whose Claude is waiting for an answer — a permission dialog, a
 * question only a person can answer — is pinned to the topbar on its own, so it
 * is one click away from any view, and unpinned again once it is answered.
 *
 * Only a pin put there by this (`auto` on the pin) is taken away: one the reader
 * pinned stays. Unpinning an automatic pin is heard as "not now" — it is not put
 * back until that Claude has been answered and asks again. One on screen is not
 * pinned; it is if the reader leaves it still unanswered.
 *
 * The tmux view feeds its own poll in (`noteSessions`); on any other view
 * `useAutoPin()` polls, for an admin on a board that offers the tmux view.
 */
import { onBeforeUnmount, ref, watch } from "vue";
import { api } from "./api.js";
import { startPoll, type Poll } from "./poll.js";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import type { Pin } from "@shared/settings.js";
import type { TmuxSession } from "@shared/tmux.js";

const POLL_MS = 5_000;

/** The sessions whose Claude is waiting, as last read: their pills are marked. */
export const waitingSessions = ref<ReadonlySet<string>>(new Set());

/** Sessions already pinned (or found pinned, or unpinned by the reader) since
 *  they last began to wait. Cleared as each stops waiting. */
const handled = new Set<string>();

/** Take a fresh session list: pin what began to wait, unpin what was answered. */
export function noteSessions(list: readonly TmuxSession[]): void {
  const viewport = useViewport();
  const waiting = new Set(list.filter((s) => s.claude?.state === "waiting").map((s) => s.name));
  waitingSessions.value = waiting;
  for (const name of handled) if (!waiting.has(name)) handled.delete(name);

  const onScreen = viewport.view === "tmux" ? viewport.tmuxShown?.session ?? null : null;
  let pins: Pin[] = viewport.pins.filter((p) => !(p.kind === "session" && p.auto && !waiting.has(p.name)));
  for (const s of list) {
    if (!waiting.has(s.name) || handled.has(s.name)) continue;
    if (pins.some((p) => p.kind === "session" && p.name === s.name)) {
      handled.add(s.name);
      continue;
    }
    if (s.name === onScreen) continue;
    pins = [...pins, { kind: "session", name: s.name, taskId: s.taskId ?? null, auto: true }];
    handled.add(s.name);
  }
  if (pins.length !== viewport.pins.length || pins.some((p, i) => p !== viewport.pins[i])) viewport.pins = pins;
}

/** Polls the session list while the tmux view, which polls it itself, is not on
 *  screen. Only for someone who may see it, and only while the tab is visible
 *  (lib/poll.ts, which also slows it while the server is slow). */
export function useAutoPin(): void {
  const board = useBoard();
  const viewport = useViewport();
  let poll: Poll | null = null;
  /** Bumped by each stop: a read from before one is not taken. */
  let gen = 0;

  async function read(): Promise<boolean> {
    const mine = gen;
    try {
      const { sessions } = await api.get<{ sessions: TmuxSession[] }>("/api/tmux/sessions");
      // The view taking over meanwhile: its own poll has the newer word.
      if (mine === gen && poll) noteSessions(sessions);
      return true;
    } catch {
      // The bridge is down, or the network: the pins stay as they are.
      return false;
    }
  }

  function stop(): void {
    gen++;
    poll?.stop();
    poll = null;
  }

  watch(
    () => board.loaded && board.tmuxAvailable && viewport.view !== "tmux",
    (on) => {
      stop();
      if (on) poll = startPoll(read, POLL_MS);
    },
    { immediate: true },
  );

  onBeforeUnmount(stop);
}
